import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type PropsWithChildren,
  type ReactNode,
  type SetStateAction,
} from 'react'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

type TCustomBottoms = Record<string, number>

type TBottomContext = {
  bottom: number
  setCustomBottoms: Dispatch<SetStateAction<TCustomBottoms>>
}

const BottomContext = createContext<TBottomContext>({
  bottom: 0,
  setCustomBottoms: () => {
    throw new Error(
      'Cannot set custom bottoms: bottom context is not provided. Seems you forgot to wrap component within <BottomProvider />',
    )
  },
})

/**
 * BottomContext Provider
 *
 * Provides the bottom height taken by bottom elements (tabs, banners) and the bottom inset
 */
export const BottomProvider = ({ children }: PropsWithChildren): ReactNode => {
  const [customBottoms, setCustomBottoms] = useState<TCustomBottoms>({})
  const { bottom: insetBottom } = useSafeAreaInsets()

  const value = useMemo(() => {
    const customBottom = Object.values(customBottoms).reduce((acc, curr) => acc + curr, 0)
    return { bottom: Math.max(customBottom, insetBottom), setCustomBottoms }
  }, [customBottoms, insetBottom])

  return <BottomContext.Provider value={value}>{children}</BottomContext.Provider>
}

export const useBottom = () => useContext(BottomContext)

export const useAddBottom = (key: string, addedBottom: number) => {
  const { setCustomBottoms } = useBottom()

  useEffect(() => {
    // Add bottomHeight for key. React 19 Suspense reconnection re-runs effects
    // without cleanup, so duplicate keys are expected and silently overwritten.
    setCustomBottoms((customBottoms) => ({ ...customBottoms, [key]: addedBottom }))

    return () => {
      // Remove bottomHeight for key on unmount
      setCustomBottoms((bottoms) => {
        const customBottoms = { ...bottoms }
        delete customBottoms[key]
        return customBottoms
      })
    }
  }, [addedBottom, key, setCustomBottoms])
}
